import { useState, useRef, useEffect } from "preact/hooks"
import { Send, Square, History, Paperclip, X, FileText } from "lucide-react"
import { DeductionSearchBar, AttachedDeduction } from "./DeductionAttach"

interface ChatInputProps {
  onSend: (content: string, attachedDeduction?: AttachedDeduction, files?: File[]) => void
  isStreaming: boolean
  onShowHistory: () => void
  /** When set, populates the input. Cleared after being consumed. */
  pendingPrompt?: string | null
  onPendingPromptConsumed?: () => void
  className?: string
}

export function ChatInput({ onSend, isStreaming, onShowHistory, pendingPrompt, onPendingPromptConsumed, className }: ChatInputProps) {
  const [value, setValue] = useState("")
  const [attachedDeduction, setAttachedDeduction] = useState<AttachedDeduction | null>(null)
  const [files, setFiles] = useState<File[]>([])
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // When a pending prompt arrives, populate the input and focus it
  useEffect(() => {
    if (pendingPrompt) {
      setValue(pendingPrompt)
      onPendingPromptConsumed?.()
      // Defer resize to next frame so the DOM reflects the new value
      requestAnimationFrame(() => {
        if (textareaRef.current) {
          textareaRef.current.style.height = "auto"
          textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + "px"
          textareaRef.current.focus()
        }
      })
    }
  }, [pendingPrompt])

  function handleSubmit() {
    const trimmed = value.trim()
    if (!trimmed || isStreaming) return
    onSend(trimmed, attachedDeduction || undefined, files.length > 0 ? files : undefined)
    setValue("")
    setAttachedDeduction(null)
    setFiles([])
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"
    }
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  function handleInput(e: Event) {
    const target = e.target as HTMLTextAreaElement
    setValue(target.value)
    target.style.height = "auto"
    target.style.height = Math.min(target.scrollHeight, 200) + "px"
  }

  function handleFileSelect(e: Event) {
    const input = e.target as HTMLInputElement
    if (input.files) {
      setFiles((prev: File[]) => [...prev, ...Array.from(input.files!)])
    }
    // Reset so the same file can be re-selected
    input.value = ""
  }

  function removeFile(index: number) {
    setFiles((prev: File[]) => prev.filter((_: File, i: number) => i !== index))
  }

  function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  useEffect(() => {
    textareaRef.current?.focus()
  }, [])

  return (
    <div class={`relative ${className || ""}`}>
      <div class="bg-white border border-gray-300 rounded-xl shadow-sm focus-within:ring-2 focus-within:ring-primary focus-within:border-primary">
        {/* File chips - shown above the textarea when files are attached */}
        {files.length > 0 && (
          <div class="flex flex-wrap gap-1.5 px-3 pt-2">
            {files.map((file: File, index: number) => (
              <div
                key={`${file.name}-${index}`}
                class="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-md bg-gray-100 border border-gray-200 text-xs"
              >
                <FileText className="w-3 h-3 text-gray-400 shrink-0" />
                <span class="text-gray-700 truncate max-w-[150px]">{file.name}</span>
                <span class="text-gray-400">{formatFileSize(file.size)}</span>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  class="p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Main prompt row */}
        <div class="flex items-end gap-2 px-3 py-2">
          <button
            type="button"
            onClick={onShowHistory}
            class="shrink-0 p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600"
            title="Conversation history"
          >
            <History className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            class="shrink-0 p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            title="Attach files"
            disabled={isStreaming}
          >
            <Paperclip className="w-5 h-5" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            class="hidden"
            accept=".pdf,.csv,.xlsx,.xls,.png,.jpg,.jpeg,.gif,.webp,.txt,.doc,.docx"
            onChange={handleFileSelect}
          />
          <textarea
            ref={textareaRef}
            value={value}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            placeholder="Ask the Wizard anything about your deductions..."
            class="flex-1 resize-none border-0 bg-transparent text-gray-800 placeholder-gray-400 text-sm focus:outline-none focus:ring-0 max-h-[200px]"
            rows={1}
            disabled={isStreaming}
          />
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!value.trim() || isStreaming}
            class={`shrink-0 p-1.5 rounded-lg transition-colors ${
              value.trim() && !isStreaming
                ? "bg-primary text-white hover:opacity-90"
                : "bg-gray-100 text-gray-400"
            }`}
          >
            {isStreaming ? (
              <Square className="w-5 h-5" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Deduction search / chip row */}
        <div class="border-t border-gray-100 px-3 py-1.5">
          <DeductionSearchBar
            selectedDeduction={attachedDeduction}
            onSelect={(ded: AttachedDeduction) => setAttachedDeduction(ded)}
            onClear={() => setAttachedDeduction(null)}
          />
        </div>
      </div>

      <p class="text-xs text-gray-400 text-center mt-2">
        Wizard may make mistakes. Always verify important information.
      </p>
    </div>
  )
}
